import React from 'react';
import { useTranslation } from "react-i18next";

function RGPDFeature({ title, description, icon }) {
  return (
    <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
      <div className="flex items-start gap-4">
        <div className="bg-blue-50 p-3 rounded-lg text-blue-600">
          {icon}
        </div>
        <div>
          <h3 className="font-bold text-lg mb-2">{title}</h3>
          <p className="text-gray-600 text-sm">{description}</p>
        </div>
      </div>
    </div>
  );
}

function RGPDSection() {
  const { t } = useTranslation("global");

  const handleRGPDPricingClick = () => {
    // Save the selection preferences to localStorage
    localStorage.setItem('pricingPreferences', JSON.stringify({
      level: 2,
      theme: 'RGPD',
      timestamp: Date.now() // Add timestamp to know how recent this selection is
    }));
  };
  
  // RGPD features
  const rgpdFeatures = [
    {
      title: t("rgpd.feature1-title", "Conformité RGPD"),
      description: t("rgpd.feature1-description", "Évaluez votre conformité au Règlement Général sur la Protection des Données et identifiez les axes d'amélioration."),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: t("rgpd.feature2-title", "Droits des personnes"),
      description: t("rgpd.feature2-description", "Vérifiez si votre organisation respecte les droits des personnes concernées (accès, rectification, effacement, etc.)."),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      title: t("rgpd.feature3-title", "Gestion des violations"),
      description: t("rgpd.feature3-description", "Évaluez votre capacité à détecter, signaler et traiter les violations de données personnelles."),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      )
    },
    {
      title: t("rgpd.feature4-title", "Documentation"),
      description: t("rgpd.feature4-description", "Vérifiez si votre documentation RGPD est complète (registre des traitements, analyse d'impact, etc.)."),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    }
  ];
  
  return (
    <section id="rgpd" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-black">
            {t("rgpd.section-title", "Protection des Données (RGPD)")}
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto mt-4">
            {t("rgpd.section-description", "Avec le Niveau 2 de notre plateforme, évaluez votre conformité au Règlement Général sur la Protection des Données et identifiez les actions à mettre en place")}
          </p>
          <div className="w-20 h-1 bg-blue-600 mx-auto mt-4"></div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {rgpdFeatures.map((feature, index) => (
            <RGPDFeature
              key={index}
              title={feature.title}
              description={feature.description}
              icon={feature.icon}
            />
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-gray-600 max-w-3xl mx-auto mb-6">
            {t("rgpd.bottom-description", "Le RGPD est un enjeu majeur pour toutes les entreprises. Notre questionnaire de 40 questions vous permet d'évaluer votre conformité et d'identifier les axes d'amélioration.")}
          </p>
          <a 
            href="/pricing" 
            onClick={handleRGPDPricingClick}
            className="inline-block bg-blue-600 text-white py-3 px-8 rounded-full font-semibold transition-all hover:bg-blue-700 hover:shadow-md"
          >
            {t("rgpd.cta-button", "Découvrir notre offre RGPD")}
          </a>
        </div>
      </div>
    </section>
  );
}

export default RGPDSection;